/**
 * URL builders for public booking pages.
 *
 * Two variant builders + a ladder helper. Each returns null when its inputs
 * are missing — callers must handle that case (hide the row, show a toast).
 *
 * Trailing slashes on baseUrl are stripped so the final URL never has //.
 */

function stripTrailing(baseUrl: string): string {
  return baseUrl.replace(/\/+$/, '');
}

function isUsable(value: string | null | undefined): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

/** `${base}/${globalSlug}/` — null when globalSlug is empty/null/undefined/whitespace. */
export function buildGlobalSlugUrl(
  baseUrl: string,
  globalSlug: string | null | undefined
): string | null {
  if (!isUsable(globalSlug)) {
    return null;
  }
  return `${stripTrailing(baseUrl)}/${globalSlug}/`;
}

/** `${base}/m/${shortCode}/` — null when shortCode is empty/null/undefined/whitespace. */
export function buildShortCodeUrl(
  baseUrl: string,
  shortCode: string | null | undefined
): string | null {
  if (!isUsable(shortCode)) {
    return null;
  }
  return `${stripTrailing(baseUrl)}/m/${shortCode}/`;
}

/**
 * Strip protocol from a URL, returning the bare domain + path.
 * Example: "https://meet.ahoy.ai" → "meet.ahoy.ai"
 */
export function stripProtocol(url: string): string {
  return url.replace(/^https?:\/\//, '');
}

export interface BuildPreferredInput {
  baseUrl: string;
  globalSlug?: string | null;
  shortCode?: string | null;
}

/**
 * Best-available URL using the ladder:
 *   global_slug → short_code.
 * Returns null when no identifier is usable.
 */
export function buildPreferredBookingUrl(
  input: BuildPreferredInput
): string | null {
  return (
    buildGlobalSlugUrl(input.baseUrl, input.globalSlug) ??
    buildShortCodeUrl(input.baseUrl, input.shortCode)
  );
}
